import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionMeta,
  CreateAlertTemplatesResult,
  MyWhatsappAlerts,
  MyWhatsappAlertsInput,
  PlatformAlertDelivery,
  PlatformAlertDeliveryQuery,
  PlatformMessaging,
  PlatformMessagingOptions,
  UpdatePlatformMessagingInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['admin', 'platform-messaging'] as const;
const MY_KEY = ['me', 'whatsapp-alerts'] as const;

export function usePlatformMessaging() {
  return useQuery<PlatformMessaging, ApiClientError>({
    queryKey: KEY,
    queryFn: () => apiClient.get<PlatformMessaging>('/admin/platform-messaging'),
  });
}

export function usePlatformMessagingOptions() {
  return useQuery<PlatformMessagingOptions, ApiClientError>({
    queryKey: [...KEY, 'options'],
    queryFn: () => apiClient.get<PlatformMessagingOptions>('/admin/platform-messaging/options'),
  });
}

export function useSavePlatformMessaging() {
  const qc = useQueryClient();
  return useMutation<PlatformMessaging, ApiClientError, UpdatePlatformMessagingInput>({
    mutationFn: (input) => apiClient.put<PlatformMessaging>('/admin/platform-messaging', input),
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}

export function useCreateAlertTemplates() {
  const qc = useQueryClient();
  return useMutation<CreateAlertTemplatesResult, ApiClientError, void>({
    mutationFn: () =>
      apiClient.post<CreateAlertTemplatesResult>('/admin/platform-messaging/templates'),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSendTestAlert() {
  const qc = useQueryClient();
  return useMutation<PlatformAlertDelivery, ApiClientError, string>({
    mutationFn: (alertType) =>
      apiClient.post<PlatformAlertDelivery>('/admin/platform-messaging/test', { alertType }),
    onSettled: () => qc.invalidateQueries({ queryKey: [...KEY, 'deliveries'] }),
  });
}

export function useAlertDeliveries(query: PlatformAlertDeliveryQuery) {
  return useQuery<{ items: PlatformAlertDelivery[]; meta: CollectionMeta }, ApiClientError>({
    queryKey: [...KEY, 'deliveries', query],
    queryFn: async () => {
      const envelope = await apiClient.getCollection<PlatformAlertDelivery>(
        '/admin/platform-messaging/deliveries',
        { query: { ...query } },
      );
      return { items: envelope.data, meta: envelope.meta };
    },
  });
}

export function useMyWhatsappAlerts() {
  return useQuery<MyWhatsappAlerts, ApiClientError>({
    queryKey: MY_KEY,
    queryFn: () => apiClient.get<MyWhatsappAlerts>('/me/whatsapp-alerts'),
  });
}

export function useSaveMyWhatsappAlerts() {
  const qc = useQueryClient();
  return useMutation<MyWhatsappAlerts, ApiClientError, MyWhatsappAlertsInput>({
    mutationFn: (input) => apiClient.put<MyWhatsappAlerts>('/me/whatsapp-alerts', input),
    onSuccess: (data) => qc.setQueryData(MY_KEY, data),
  });
}
